import React, { useState, useEffect, useCallback } from 'react';
import { 
  ISegmentComponent,
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';
import { DfFormComments } from '../df-form-comments';
import './df-form-segment.scss';

interface DfFormSegmentProps {
  id: string;
  properties: ISegmentComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: string;
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
  showCommentsInPreview?: boolean;
}

export const DfFormSegment: React.FC<DfFormSegmentProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = '',
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'preview',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false,
  showCommentsInPreview = false
}) => {
  const [selectedValue, setSelectedValue] = useState<string>(formValue);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [showCommentInput, setShowCommentInput] = useState<boolean>(false);

  // Initialize comment from properties when in preview mode (for form submissions)
  useEffect(() => {
    if (mode === 'preview' && showCommentsInPreview && properties?.basic?.comments) {
      setComment(properties.basic.comments);
    }
  }, [mode, showCommentsInPreview, properties]);

  // Validate field
  const validateField = useCallback((value: string): { isValid: boolean; errors: Record<string, any> } => {
    const errors: Record<string, any> = {};
    let isValid = true;

    // Required validation
    if (properties?.validation?.required && (!value || value.trim() === '')) {
      errors.required = true;
      isValid = false;
    }

    // Comment validation for fail/no options
    const selectedOption = properties?.options?.find(option => option.value === value);
    const isCommentRequired = selectedOption && shouldShowRedChip(selectedOption.value);
    
    if (isCommentRequired && (!comment || comment.trim() === '')) {
      errors.commentRequired = true;
      isValid = false;
    }

    return { isValid, errors };
  }, [properties, comment]);


  // Handle segment button click
  const handleSegmentClick = useCallback((optionValue: string) => {
    setSelectedValue(optionValue);
    setIsTouched(true);
    
    if (onValueChange) {
      const validation = validateField(optionValue);
      const change: IFormControlChange = {
        id,
        value: optionValue,
        isValid: validation.isValid,
        errors: validation.errors,
        comments: comment,
      } as IFormControlChange;
      onValueChange(change);
    }
  }, [id, onValueChange, validateField, comment]);

  // Handle focus
  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    setIsTouched(true);
    if (onBlur) {
      onBlur();
    }
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Update value when formValue prop changes
  useEffect(() => {
    if (formValue !== selectedValue) {
      setSelectedValue(formValue);
      // Clear comments when form value changes (new form submission)
      if (mode === 'test') {
        setComment('');
        setShowCommentInput(false);
      }
    }
  }, [formValue, selectedValue, mode]);

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Clear comments when switching to edit mode or when component is reset
  useEffect(() => {
    if (mode === 'edit') {
      setComment('');
      setShowCommentInput(false);
    }
  }, [mode]);


  // Handle comment input visibility when selected value changes
  useEffect(() => {
    const selectedOption = properties?.options?.find(option => option.value === selectedValue);
    const isCommentRequired = selectedOption && shouldShowRedChip(selectedOption.value);
    
    if (isCommentRequired && !comment) {
      // Show comment input immediately when No/Fail is selected and no comment exists
      setShowCommentInput(true);
    } else if (!isCommentRequired) {
      // Hide comment input when Yes/Pass is selected
      setShowCommentInput(false);
    }
  }, [selectedValue, properties?.options, comment]);

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get segment container class (matching Angular approach)
  const getSegmentContainerClass = (): string => {
    return properties?.basic?.inlineLayout ? 'inline-layout' : 'grid-layout';
  };

  // Check if option should show red chip (for No/Fail cases)
  const shouldShowRedChip = (optionValue: string): boolean => {
    const lowerValue = optionValue.toLowerCase();
    return lowerValue === 'no' || lowerValue === 'fail' || lowerValue === 'false';
  };

  // Check if comments section should be shown
  const shouldShowCommentsSection = (): boolean => {
    const selectedOption = properties?.options?.find(option => option.value === selectedValue);
    return selectedOption ? shouldShowRedChip(selectedOption.value) : false;
  };

  // Check if comment is required
  const isCommentRequired = (): boolean => {
    return shouldShowCommentsSection();
  };

  // Toggle comment input visibility
  const toggleCommentInput = () => {
    // Don't allow toggling in preview mode
    if (mode === 'preview') {
      return;
    }
    setShowCommentInput(!showCommentInput);
  };

  // Handle comment save (auto-save on typing)
  const handleCommentSave = (newComment: string) => {
    setComment(newComment);
    setShowCommentInput(false); // Hide input after save
    // Re-validate with new comment
    const validation = validateField(selectedValue);
    if (onValueChange) {
      const change: IFormControlChange = {
        id,
        value: selectedValue,
        isValid: validation.isValid,
        errors: validation.errors,
        comments: newComment
      } as IFormControlChange;
      onValueChange(change);
    }
  };

  // Check if comment input should be shown
  const shouldShowCommentInput = (): boolean => {
    // Don't show comment input in preview mode
    if (mode === 'preview') {
      return false;
    }
    // Show input when comments section is needed and either:
    // 1. User manually toggled it, OR
    // 2. No existing comment (so user needs to add one)
    const needsComments = shouldShowCommentsSection();
    const hasNoComment = !comment || comment.trim() === '';
    return needsComments && (showCommentInput || hasNoComment);
  };

  // Check if comment display should be shown
  const shouldShowCommentDisplay = (): boolean => {
    // In preview mode, show comments if they exist and showCommentsInPreview is true
    if (mode === 'preview' && showCommentsInPreview && comment) {
      return true;
    }
    const result = shouldShowCommentsSection() && !showCommentInput && !!comment;
    return result;
  };

  // Check if notes should be shown for non-required options
  const shouldShowNotesForNonRequired = (): boolean => {
    // In preview mode, don't show here - let shouldShowCommentDisplay handle it
    if (mode === 'preview') {
      return false;
    }
    const result = !shouldShowCommentsSection() && Boolean(comment && comment.trim() !== '');
    return result;
  };

  // Check if comments error should be shown
  const shouldShowCommentsError = (): boolean => {
    // Don't show errors in preview mode
    if (mode === 'preview') {
      return false;
    }
    // Show error when comments are required but no comment exists
    // Only show after user has selected the option (similar to form validation)
    return shouldShowCommentsSection() && isCommentRequired() && !comment && isTouched;
  };




  // Get button type for styling (pass/fail)
  const getButtonType = (optionValue: string): 'pass' | 'fail' => {
    const value = optionValue?.toLowerCase();
    if (value === 'pass' || value === 'yes') {
      return 'pass';
    }
    if (value === 'fail' || value === 'no') {
      return 'fail';
    }
    return 'pass';
  };

  // Get button icon based on option value
  const getButtonIcon = (optionValue: string): string => {
    const buttonType = getButtonType(optionValue);
    return buttonType === 'pass' ? '✓' : '×';
  };

  const shouldDisable = disabled || readonly || properties?.validation?.readonly;
  return (
    <>
    <div className={`form-group segment-group ${getLabelAlignmentClass()} ${className}`}>
      {!hideLabel && properties.basic.label ? (
        <>
          <label className="form-label">
            {properties.basic.label}
            {properties.validation.required && (
              <span className="required-indicator">*</span>
            )}
          </label>
          
          <div className="form-segment-wrapper">
            <div className={`form-segment-container ${getSegmentContainerClass()}`}>
              {(properties.options || []).map((option, index) => {
                const isSelected = selectedValue === option.value;
                
                return (
                  <div key={index} className={`form-segment-option ${isSelected ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      id={`${id}_option_${index}`}
                      name={id}
                      value={option.value}
                      checked={isSelected}
                      onChange={() => !shouldDisable && !option.disabled && handleSegmentClick(option.value)}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      disabled={shouldDisable || option.disabled}
                      className="form-segment-radio"
                    />
                    <label
                      htmlFor={`${id}_option_${index}`}
                      className={`segment-button-label ${getButtonType(option.value) === 'pass' ? 'pass-button' : 'fail-button'}`}
                    >
                      <span className="segment-icon">{getButtonIcon(option.value)}</span>
                      <span className="segment-text">{option.value}</span>
                    </label>
                  </div>
                );
              })}
            </div>

          </div>

        </>
      ) : (
        <div className="form-segment-wrapper">
          <div className={`form-segment-container ${getSegmentContainerClass()}`}>
            {(properties.options || []).map((option, index) => {
              const isSelected = selectedValue === option.value;
              
              return (
                <div key={index} className={`form-segment-option ${isSelected ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    id={`${id}_option_${index}`}
                    name={id}
                    value={option.value}
                    checked={isSelected}
                    onChange={() => !shouldDisable && !option.disabled && handleSegmentClick(option.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    disabled={shouldDisable || option.disabled}
                    className="form-segment-radio"
                  />
                  <label
                    htmlFor={`${id}_option_${index}`}
                    className={`segment-button-label ${getButtonType(option.value) === 'pass' ? 'pass-button' : 'fail-button'}`}
                  >
                    <span className="segment-icon">{getButtonIcon(option.value)}</span>
                    <span className="segment-text">{option.value}</span>
                  </label>
                </div>
              );
            })}
          </div>
          



        </div>
       )}

      
     </div>
     <DfFormErrorMsg
            validationErrors={validationErrors}
            fieldId={id}
            touchedFields={touchedFields}
            formSubmitted={formSubmitted}
            properties={properties}
            localValidation={{
              isValid: validateField(selectedValue).isValid,
              errors: Object.fromEntries(
                Object.entries(validateField(selectedValue).errors).filter(
                  ([key]) => key !== 'commentRequired'
                )
              )
            }}
            isTouched={isTouched}
            mode={mode}
          />
      {/* Comments Section - Below segment buttons */}
      {(!shouldDisable || (mode === 'preview' && showCommentsInPreview)) && (
        <>
          {/* Show saved comment display when not in comment input mode (for fail/no options) */}
          {shouldShowCommentDisplay() && (
            <div onClick={mode === 'preview' ? undefined : toggleCommentInput} style={{ cursor: mode === 'preview' ? 'default' : 'pointer' }}>
              <label className="form-label notes-label-text">
                Notes: {comment}
              </label>
            </div>
          )}

          {/* Show comment input when needed (for fail/no options) */}
          {shouldShowCommentInput() && (
            <DfFormComments
              comment={comment}
              onSave={handleCommentSave}
              placeholder="Enter your reason..."
              disabled={shouldDisable}
            />
          )}

          {/* Show saved comments for non-fail/no options (only if comment exists) */}
          {shouldShowNotesForNonRequired() && (
            <div>
              <label className="form-label notes-label-text">
                Notes: {comment}
              </label>
            </div>
          )}

          {/* Show error message when comment is required but not provided */}
          {shouldShowCommentsError() && (
            <div>
              <label className="form-label notes-label-text error-message no-border">
                Notes are required
              </label>
            </div>
          )}
        </>
        
      )}
     </>
     
   );
};



export default DfFormSegment;
